/**
 * The chosen orders in Bestellingen (prototype routeSel and ORD.last): a click chooses one order,
 * Ctrl/⌘-click adds or removes one (with several chosen a plain click does too), Shift-click adds the
 * range from the last one clicked, and the boxes in front of the rows (and the one in the header) do the
 * same by touch.
 */
export interface Selection {
  ids: ReadonlySet<string>;
  /** The last order clicked or ticked: where a Shift-click range starts. */
  last: string | null;
}

export const NOTHING: Selection = { ids: new Set(), last: null };

/** A click on a row; `order` is the list as shown, the newest first. */
export function clickRow(
  selection: Selection,
  order: readonly string[],
  id: string,
  keys: { shift: boolean; toggle: boolean },
): Selection {
  if (keys.shift && selection.last !== null) {
    const from = order.indexOf(selection.last);
    const to = order.indexOf(id);
    if (from >= 0 && to >= 0) {
      const ids = new Set(selection.ids);
      for (const each of order.slice(Math.min(from, to), Math.max(from, to) + 1)) ids.add(each);
      return { ids, last: selection.last };
    }
  }
  // with several chosen, a plain click adds or removes one: a tap beside the box never loses the
  // selection (Furkan, 11/10/2026); "Selectie wissen" empties it
  if (keys.toggle || selection.ids.size > 1) return tickRow(selection, id, !selection.ids.has(id));
  return { ids: new Set([id]), last: id };
}

/** The box in front of a row. */
export function tickRow(selection: Selection, id: string, on: boolean): Selection {
  const ids = new Set(selection.ids);
  if (on) ids.add(id);
  else ids.delete(id);
  return { ids, last: id };
}

/** The box in the header: every order, or none. */
export function tickAll(selection: Selection, order: readonly string[], on: boolean): Selection {
  return on ? { ids: new Set(order), last: selection.last } : NOTHING;
}

/** Orders that left the list (deleted) leave the selection too. */
export function keepExisting(selection: Selection, order: readonly string[]): Selection {
  const existing = new Set(order);
  if ([...selection.ids].every((id) => existing.has(id))) return selection;
  return {
    ids: new Set([...selection.ids].filter((id) => existing.has(id))),
    last: selection.last !== null && existing.has(selection.last) ? selection.last : null,
  };
}
